import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchOptions } from './hooks/useFetchOptions'
import { useGenerateReport } from './hooks/useGenerateReport'
import {
  AccordionMultiSelect,
  AcknowledgeModal,
  ActionModal,
  Button,
  Flex,
  Modal,
  ModalTitle,
  SelectOption,
  Spinner
} from '@percent/lemonade'
import { useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { CANDIDATE_DETAILS, DISBURSEMENTS } from './hardcodedQuestions'

export function CandidatesReport() {
  const { t } = useTranslation()

  const { id: programId } = useRequiredParams()

  const [questionIds, setQuestionIds] = useState<string[]>([])
  const [candidateDetails, setCanidateDetails] = useState<Array<typeof CANDIDATE_DETAILS[number]['value']>>([
    'id',
    'organisation.name',
    'currentState'
  ])
  const [disbursements, setDisbursements] = useState<Array<typeof DISBURSEMENTS[number]['value']>>([])

  const [modalState, setModalState] = useState<'action' | 'generating' | 'generated'>('action')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { taskTemplates, isLoadingQuestions, questionsError } = useFetchOptions({
    programId
  })

  const orderedTaskTemplates = taskTemplates?.toReversed() ?? []

  const { isLoadingReport, reportError, generatingError, resetGeneratingError, generateReport } = useGenerateReport({
    programId,
    taskTemplates: orderedTaskTemplates,
    currentState: undefined,
    displayGroup: undefined,
    questionIds,
    candidateDetails,
    disbursements,
    onGeneratingStart: () => setModalState('generating'),
    onGeneratingComplete: () => setModalState('generated')
  })

  const resetQuestions = () => {
    setQuestionIds([])
    setCanidateDetails([])
    setDisbursements([])
  }
  const reset = () => {
    setModalState('action')
    resetQuestions()
    resetGeneratingError()
    setIsModalOpen(false)
  }
  const error = questionsError || reportError || generatingError

  const questionOptions: Record<string, SelectOption[]> = orderedTaskTemplates.reduce((acc, template) => {
    acc[template.taskTemplate.name] = template.questions.map(q => ({
      value: q.id,
      label: q.dashboardLabel ?? q.label
    }))

    return acc
  }, {})

  const handleSelectionChange = (newValue: string[]) => {
    const candidateDetailsValues = CANDIDATE_DETAILS.filter(q => newValue.includes(q.value)).map(q => q.value)
    setCanidateDetails(candidateDetailsValues)
    const disbursementsValues = DISBURSEMENTS.filter(q => newValue.includes(q.value)).map(q => q.value)
    setDisbursements(disbursementsValues)
    const questionValues = newValue.filter(
      v => !(candidateDetailsValues as string[]).includes(v) && !(disbursementsValues as string[]).includes(v)
    )
    setQuestionIds(questionValues)
  }

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        variant="secondary"
        data-testid="open-candidates-report-modal"
        startIcon="download-circle"
      >
        {t('table.candidatesReport.trigger')}
      </Button>
      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setModalState('action')
        }}
        data-testid="candidates-report-modal"
        showModalOverflow={false}
      >
        {!error && modalState === 'action' && (
          <ActionModal
            title={t('table.candidatesReport.title')}
            primaryButtonText={t('table.candidatesReport.submit')}
            loading={isLoadingQuestions || isLoadingReport}
            handleClose={() => setIsModalOpen(false)}
            handleSubmit={generateReport}
            primaryBtnTestId="generate-report-btn"
            secondaryButtonText={t('button.cancel')}
            secondaryBtnTestId="cancel-generate-report-btn"
            disabled={
              isLoadingQuestions ||
              (candidateDetails.length === 0 && questionIds.length === 0 && disbursements.length === 0)
            }
            maxHeight="60vh"
          >
            <AccordionMultiSelect
              data={{
                'Candidate Details': [...CANDIDATE_DETAILS],
                ...questionOptions,
                Disbursements: [...DISBURSEMENTS]
              }}
              data-testid="candidates-report-multi-select"
              value={[...candidateDetails, ...questionIds, ...disbursements]}
              onChange={handleSelectionChange}
            />
          </ActionModal>
        )}
        {!error && modalState === 'generating' && (
          <Flex gap={16} p={32} align="center" direction="column" data-testid="generating-report-spinner">
            <ModalTitle>{t('table.candidatesReport.generating')}</ModalTitle>
            <Spinner size="m" />
          </Flex>
        )}
        {!error && modalState === 'generated' && (
          <AcknowledgeModal
            result="positive"
            title={t('table.candidatesReport.generated')}
            buttonText={t('button.close')}
            handleClose={reset}
          />
        )}
        {error && modalState === 'action' && (
          <AcknowledgeModal
            result="negative"
            title={t('table.candidatesReport.errors.failedToFetchOptions')}
            buttonText={t('button.close')}
            handleClose={reset}
          >
            {error.message}
          </AcknowledgeModal>
        )}
        {error && modalState === 'generating' && (
          <AcknowledgeModal
            result="negative"
            title={t('table.candidatesReport.errors.failedToGenerate')}
            buttonText={t('button.close')}
            handleClose={reset}
          >
            {error.message}
          </AcknowledgeModal>
        )}
      </Modal>
    </>
  )
}
